import {
  Stack,
  Box,
  Typography,
  Chip,
  Avatar,
  Paper,
  Divider,
  Grid,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CopyIcon from "@mui/icons-material/FileCopy";
import copy from "copy-to-clipboard";
import { useSnackbar } from "notistack";
import type { UserShowData } from "@/admin/entities/users";
import { CustomCellDate, CrudLink } from "@/shared/components";
import { AttributionTouchList } from "@/shared/components/AttributionTouch";
import { SudoButton } from "@/admin/shared/components/SudoButton";
import { UserOrganizations } from "../UserOrganizations";

interface UserDetailsProps {
  user: UserShowData;
}

export function UserDetails({ user }: UserDetailsProps) {
  const { enqueueSnackbar } = useSnackbar();

  if (!user) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          Пользователь не найден
        </Typography>
      </Paper>
    );
  }

  const fullName = [user.firstName, user.middleName, user.lastName]
    .filter(Boolean)
    .join(" ");

  const formatDate = (iso: string) => new Date(iso).toLocaleString("ru-RU");

  const getInitials = () => {
    const firstName = user.firstName?.charAt(0) || "";
    const lastName = user.lastName?.charAt(0) || "";
    const initials = (firstName + lastName).toUpperCase();
    if (initials) return initials;

    if (user.email) {
      return user.email.split("@")[0].charAt(0).toUpperCase();
    }

    if (user.phone) {
      return user.phone.slice(-1);
    }

    return "U";
  };

  // isAdmin/isPartner могут быть выставлены одновременно на одной служебной
  // записи (пространства аккаунтов — см. docs/superpowers/specs/2026-07-31-users-roles-design.md),
  // isPlatformAdmin — только у платформенного двойника — показываем все
  // применимые роли независимо друг от друга, а не одну главную.
  const roleChips: {
    label: string;
    color: "error" | "primary" | "secondary" | "default";
    variant: "filled" | "outlined";
  }[] = [];
  if (user.isSuperAdmin) {
    roleChips.push({ label: "Суперадмин", color: "error", variant: "filled" });
  } else if (user.isAdmin) {
    roleChips.push({
      label: "Администратор",
      color: "primary",
      variant: "filled",
    });
  }
  if (user.isPartner) {
    roleChips.push({ label: "Партнёр", color: "secondary", variant: "filled" });
  }
  if (user.isPlatformAdmin) {
    roleChips.push({
      label: "Модератор платформы",
      color: "default",
      variant: "outlined",
    });
  }
  if (!roleChips.length) {
    roleChips.push({
      label: "Пользователь",
      color: "default",
      variant: "filled",
    });
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        {/* Основная информация */}
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Avatar sx={{ width: 52, height: 52 }}>{getInitials()}</Avatar>
            <Box>
              <Typography variant="h6">
                {fullName ||
                  user.displayName ||
                  user.email ||
                  user.phone ||
                  "Пользователь"}
              </Typography>
              {user.displayName && fullName && (
                <Typography variant="body2" color="textSecondary">
                  {user.email || user.phone}
                </Typography>
              )}
            </Box>
          </Stack>

          {(user.sudoPartnerUrl || user.sudoSiteUrl) && (
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              mb={2}
            >
              {user.sudoPartnerUrl && (
                <SudoButton
                  href={user.sudoPartnerUrl}
                  label="Войти в кабинет партнёра"
                />
              )}
              {user.sudoSiteUrl && (
                <SudoButton
                  href={user.sudoSiteUrl}
                  label="Открыть витрину как пользователь"
                />
              )}
            </Stack>
          )}

          <Stack spacing={1.5}>
            {user.email && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{user.email}</Typography>
              </Box>
            )}

            {user.phone && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Номер телефона
                </Typography>
                <Typography variant="body1">{user.phone}</Typography>
              </Box>
            )}

            {fullName && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Полное имя
                </Typography>
                <Typography variant="body1">{fullName}</Typography>
              </Box>
            )}

            {user.displayName && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Отображаемое имя
                </Typography>
                <Typography variant="body1">{user.displayName}</Typography>
              </Box>
            )}

            <Box>
              <Typography variant="body2" color="text.secondary">
                Роль
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {roleChips.map((chip) => (
                  <Chip
                    key={chip.label}
                    label={chip.label}
                    color={chip.color}
                    variant={chip.variant}
                    size="small"
                  />
                ))}
              </Stack>
            </Box>

            {user.platform && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Платформа
                </Typography>
                <Typography variant="body1">
                  <CrudLink
                    id={user.platform.id}
                    tab="details"
                    path="/admin/platforms"
                  >
                    {user.platform.title}
                  </CrudLink>
                </Typography>
              </Box>
            )}

            {user.currentOrganization && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Текущая организация
                </Typography>
                <Typography variant="body1">
                  <CrudLink
                    id={user.currentOrganization.id}
                    tab="details"
                    path="/admin/organizations"
                  >
                    {user.currentOrganization.title}
                  </CrudLink>
                </Typography>
              </Box>
            )}

            {/* Токен для API под пользователем, как в референсе
                (partner/src/admin/components/UserDetails): передаётся в
                заголовке Authorization или x-auth-token. */}
            {user.apiAuthToken && (
              <TextField
                fullWidth
                size="small"
                label="API токен"
                value={user.apiAuthToken}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          copy(user.apiAuthToken);
                          enqueueSnackbar("Токен скопирован", {
                            variant: "success",
                          });
                        }}
                        size="small"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {/* Системная информация */}
            <Box>
              <Typography variant="body2" color="text.secondary">
                ID
              </Typography>
              <Typography variant="body1">{user.id}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Дата создания
              </Typography>
              <CustomCellDate date={user.createdAt} />
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Дата обновления
              </Typography>
              <CustomCellDate date={user.updatedAt} />
            </Box>

            {user.deletedAt && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Дата удаления
                </Typography>
                <CustomCellDate date={user.deletedAt} />
              </Box>
            )}
          </Stack>
        </Box>

        <Box>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom>
                Откуда пришёл
              </Typography>
              <AttributionTouchList
                touch={user.registrationUtm}
                formatDate={formatDate}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom>
                Последнее касание
              </Typography>
              <AttributionTouchList
                touch={user.lastUtm}
                formatDate={formatDate}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Организации, где юзер сотрудник (OrganizationUser по userId) —
            членством управляют со стороны вкладки "Сотрудники" организации,
            здесь только чтение. */}
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Организации
          </Typography>
          <UserOrganizations id={user.id} />
        </Box>
      </Stack>
    </Paper>
  );
}
